import React, { useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Rocket, Pause, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBrand } from '@/hooks/useBrand'
import GoLiveConfirmDialog from './GoLiveConfirmDialog'
import { groupHotelCount } from '@/lib/reporting-scope'
import type { ReportingScope } from '@/types/shared'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface GoLiveControlProps {
  /** The property's backend-served canonical path (`property.basePath`). */
  propertyBasePath: string
}

// Shared props this control reads (set by Operator::BaseController#shared_props, claim-15).
// Index signature satisfies Inertia's usePage<T extends PageProps> constraint.
interface GoLiveSharedProps {
  dryRun?: boolean
  liveAllowed?: boolean
  canGoLive?: boolean
  reportingScope?: ReportingScope | null
  [key: string]: unknown
}

/**
 * claim-15 — the per-property Go-live banner. PAUSED STATE ONLY.
 *
 * A paused property is an unfinished setup, so it earns a loud, full-width banner with a
 * standing call to action. A LIVE property is just working — there is nothing to do, so the
 * banner self-hides and the quiet header indicator (PushStatusBadge) carries the state.
 * Pausing moved into that badge's popover, where it is password-confirmed; keeping a
 * standing "Pause" CTA on the dashboard made stopping rate pushes look like a routine
 * next step rather than the consequential action it is.
 *
 * This banner is a TRANSIENT conversion/alerting surface, not the home of go-live: the header
 * badge is the permanent entrypoint and offers the same action. Both open the shared
 * GoLiveConfirmDialog, so neither has to redirect to the other.
 *
 * Owner/admin only (canGoLive); the action is hidden for managers/viewers because the
 * backend CanCanCan deny seam 403s them regardless. When the account's billing lapses
 * (liveAllowed === false) the go-live action is disabled with an explanatory Tooltip —
 * the entitlement gate also lives server-side in ToggleGoLiveMutation, so this is purely
 * a display hint.
 *
 * Reads dryRun / liveAllowed / canGoLive from shared props (Operator::BaseController).
 * Hits PATCH <propertyBasePath>/go-live (the claim-14 route) via Inertia's router (CSRF
 * is handled by Inertia). No frontend math, no monetary values.
 */
export default function GoLiveControl({ propertyBasePath }: GoLiveControlProps) {
  const { props } = usePage<GoLiveSharedProps>()
  const brand = useBrand()
  const { dryRun, liveAllowed, canGoLive, reportingScope } = props

  const [confirmGoLive, setConfirmGoLive] = useState(false)

  // Go-live is an owner/admin-only intent — the deny seam 403s everyone else.
  if (!canGoLive) return null
  // Live properties get no banner. Status lives in the header badge; pausing lives in its
  // popover. Guard on `=== false` so an absent prop never silently hides the CTA.
  if (dryRun === false) return null

  const billingBlocked = liveAllowed === false

  // LOCKED RULE 7 — publishing stays per listing, with per-listing gates. A paused hotel inside a
  // live group still computes and still shows prices; a group changes nothing about the gate. Say
  // that here, where the operator is looking at the pause, rather than leaving him to infer that
  // his group's prices have stopped too.
  const groupNote =
    reportingScope && groupHotelCount(reportingScope) > 1
      ? 'Prices here are still set with the rest of the combined inventory. Each listing publishes through its own switch, so only this one is held back.'
      : null

  const goLiveButton = (
    <Button
      onClick={() => setConfirmGoLive(true)}
      disabled={billingBlocked}
      className="shrink-0 rounded-lg bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-emerald-500 disabled:opacity-60"
    >
      {billingBlocked ? (
        <Lock className="mr-1.5 h-3.5 w-3.5" />
      ) : (
        <Rocket className="mr-1.5 h-3.5 w-3.5" />
      )}
      Go live
    </Button>
  )

  return (
    <div className="animate-slide-in flex flex-col gap-4 rounded-xl border border-amber-200/70 bg-gradient-to-r from-amber-50 via-amber-50/70 to-orange-50/40 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
          <Pause className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-amber-900">
              Paused — rates not being pushed
            </h3>
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              Paused
            </span>
          </div>
          <p className="mt-0.5 text-sm text-amber-700">
            {billingBlocked
              ? 'Billing is not active for this account. Activate billing to take this property live.'
              : `Computed rates stay in ${brand}. Take this property live to start pushing to your PMS.`}
          </p>
          {groupNote && <p className="mt-1 max-w-prose text-xs text-amber-700/90">{groupNote}</p>}
        </div>
      </div>

      {billingBlocked ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            {/* Wrap the disabled button so the tooltip still fires on hover. */}
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-not-allowed">{goLiveButton}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px]">
              Billing is not active for this account. Activate billing before taking a property live.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        goLiveButton
      )}

      {/* Shared with the header badge's popover — one dialog, one request, no drift. */}
      <GoLiveConfirmDialog propertyBasePath={propertyBasePath} open={confirmGoLive} onOpenChange={setConfirmGoLive} />
    </div>
  )
}
